import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export interface User {
  id: number;
  email: string;
  display_name: string;
  created_at: string;
  token_balance: number;
  subscription_tier: string;
  subscription_status: string;
  is_admin: boolean;
  email_verified: boolean;
  trading_mode: 'paper' | 'live';
  kalshi_connected: boolean;
  kalshi_api_connected: boolean;
  paper_balance: number;
  paper_initial_balance: number;
  account_balance: number;
  initial_balance: number;
  last_kalshi_sync: string | null;
  is_founding_member: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokenBalance: number;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  tokenBalance: 0,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = user !== null;
  const tokenBalance = user?.token_balance ?? 0;

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/auth/me');
      setUser(response.data);
    } catch {
      // Token invalid or expired — clear everything
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    }
  }, []);

  // On mount, check for existing token and validate it
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        await fetchUser();
      }
      setIsLoading(false);
    };
    init();
  }, [fetchUser]);

  const login = async (email: string, password: string) => {
    const response = await api.post('/api/v1/auth/login', { email, password });
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    // Fetch the full user object
    await fetchUser();
  };

  const register = async (email: string, password: string, displayName: string) => {
    const response = await api.post('/api/v1/auth/register-with-token', {
      email,
      password,
      display_name: displayName,
      tos_accepted: true,
      privacy_accepted: true,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        tokenBalance,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
